import React, { useState, useEffect } from 'react';
import { UniversalWaitingRoom } from './UniversalWaitingRoom';
import { Loader2, AlertCircle, ExternalLink } from 'lucide-react';

interface ExternalSessionRouterProps {
  sessionId: string;
}

interface ExternalSessionInfo {
  session_id: string;
  status: 'waiting' | 'accepted' | 'active' | 'completed' | 'expired' | 'cancelled';
  template_type: 'universal';
  request_id: string;
  session_url: string;
  participant_data: {
    name: string;
    email: string;
    phone?: string;
  };
  host_data: {
    name: string;
    company: string;
  };
  expires_at: string;
  created_at: string;
}

export const ExternalSessionRouter: React.FC<ExternalSessionRouterProps> = ({
  sessionId
}) => {
  const [sessionInfo, setSessionInfo] = useState<ExternalSessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  // Fetch session information
  const fetchSessionInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call the real API to get session status
      const response = await fetch(`/api/v1/external/session-status/${sessionId}`);
      if (!response.ok) {
        throw new Error('Session not found or expired');
      }
      
      const data = await response.json();
      
      // Transform API response to match our interface
      const sessionInfo: ExternalSessionInfo = {
        session_id: data.session_id,
        status: data.status,
        template_type: 'universal', // All external sessions are universal now
        request_id: data.request_id,
        session_url: data.session_url,
        participant_data: data.participant_data,
        host_data: data.host_data,
        expires_at: data.expires_at || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        created_at: data.created_at || new Date().toISOString()
      };

      setSessionInfo(sessionInfo);
      setLoading(false);

    } catch (err) {
      console.error('Error fetching session info:', err);
      setError(err instanceof Error ? err.message : 'Failed to load session');
      setLoading(false);
    }
  };



  // Handle redirect to template-specific waiting room
  const handleRedirect = (redirectUrl: string) => {
    setRedirecting(true);
    
    // Add session ID to the URL if not already present
    const url = new URL(redirectUrl, window.location.origin);
    if (!url.searchParams.has('sessionId')) {
      url.searchParams.set('sessionId', sessionId);
    }
    
    // Add external flag to indicate this is from external API
    url.searchParams.set('external', 'true');
    
    // Redirect to the template-specific waiting room
    setTimeout(() => {
      window.location.href = url.toString();
    }, 1000);
  };

  // Check if session is valid and not expired
  const isSessionValid = (sessionInfo: ExternalSessionInfo): boolean => {
    const now = new Date().getTime();
    const expiresAt = new Date(sessionInfo.expires_at).getTime();
    
    return now < expiresAt && !['expired', 'cancelled'].includes(sessionInfo.status);
  };

  useEffect(() => {
    if (sessionId) {
      fetchSessionInfo();
    } else {
      setError('Invalid session ID');
      setLoading(false);
    }
  }, [sessionId]);

  // Auto-redirect if session is valid
  useEffect(() => {
    if (sessionInfo && isSessionValid(sessionInfo) && !redirecting) {
      const redirectUrl = getTemplateWaitingRoomUrl(sessionInfo.template_type, sessionInfo.request_id);
      handleRedirect(redirectUrl);
    }
  }, [sessionInfo, redirecting]);

  // Generate template-specific waiting room URL
  const getTemplateWaitingRoomUrl = (templateType: string, requestId: string): string => {
    // All external sessions are universal now
    return `/templates/universal/waiting/${requestId}`;
  };

  // Get template display info
  const getTemplateInfo = (templateType: string) => {
    // All external sessions are universal now
    return { name: 'Universal Consultation', icon: '🌐', color: 'purple' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Session</h2>
          <p className="text-gray-600">Please wait while we prepare your consultation...</p>
        </div>
      </div>
    );
  }

  if (error || !sessionInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Session Not Found</h1>
          <p className="text-gray-600 mb-4">
            {error === 'Session not found' 
              ? 'This session link may have expired or is invalid.'
              : error || 'Unable to load session information.'
            }
          </p>
          <div className="space-y-3">
            <button
              onClick={() => fetchSessionInfo()}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
            <p className="text-sm text-gray-500">
              If you continue to have issues, please contact support with session ID: {sessionId}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check if session is expired
  if (!isSessionValid(sessionInfo)) {
    const templateInfo = getTemplateInfo(sessionInfo.template_type);
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">{templateInfo.icon}</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Session Expired</h1>
          <p className="text-gray-600 mb-4">
            This {templateInfo.name.toLowerCase()} session has expired or been cancelled.
          </p>
          <div className="bg-gray-100 rounded-lg p-4 mb-4 text-left">
            <h3 className="font-semibold text-gray-900 mb-2">Session Details:</h3>
            <div className="space-y-1 text-sm text-gray-600">
              <p><strong>Participant:</strong> {sessionInfo.participant_data.name}</p>
              <p><strong>Host:</strong> {sessionInfo.host_data.name}</p>
              <p><strong>Company:</strong> {sessionInfo.host_data.company}</p>
              <p><strong>Expired:</strong> {new Date(sessionInfo.expires_at).toLocaleString()}</p>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Please request a new session link from {sessionInfo.host_data.company}.
          </p>
        </div>
      </div>
    );
  }

  // Show redirecting state
  if (redirecting) {
    const templateInfo = getTemplateInfo(sessionInfo.template_type);
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">{templateInfo.icon}</div>
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Redirecting...</h2>
          <p className="text-gray-600 mb-4">
            Taking you to your {templateInfo.name.toLowerCase()} waiting room.
          </p>
          
          <div className="bg-blue-50 rounded-lg p-4 mb-4 text-left">
            <div className="flex items-start">
              <ExternalLink className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">What's happening:</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Connecting you to {sessionInfo.host_data.company}</li>
                  <li>• Setting up your consultation with {sessionInfo.host_data.name}</li>
                  <li>• Preparing the video session</li>
                </ul>
              </div>
            </div>
          </div>

          <button
            onClick={() => {
              const redirectUrl = getTemplateWaitingRoomUrl(sessionInfo.template_type, sessionInfo.request_id);
              const url = new URL(redirectUrl, window.location.origin);
              url.searchParams.set('sessionId', sessionId);
              url.searchParams.set('external', 'true');
              window.location.href = url.toString();
            }}
            className="text-blue-600 text-sm hover:text-blue-700 underline"
          >
            Click here if you're not redirected automatically
          </button>
        </div>
      </div>
    );
  }

  // Fallback to universal waiting room (though this shouldn't normally be reached)
  return (
    <UniversalWaitingRoom 
      sessionId={sessionId}
      onRedirect={handleRedirect}
    />
  );
};